import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Line } from 'react-chartjs-2';
import { TrendingUp } from 'lucide-react';
import { levelProgressionService } from '../../services/levelProgressionService';
import { useTheme } from '../../hooks/useTheme';
import { useLocale } from '../../hooks/useLocale';
import { LEARNER_UI_PERSIAN_STACK } from '../../constants/learnerPersianFonts';

const HOURLY_CHART_FONT_LATIN = 'system-ui, -apple-system, "Segoe UI", sans-serif';

export type HourlyLookbackDays = 7 | 30 | 180;

export interface HourlyActivityRow {
  hour?: number;
  Hour?: number;
  actionCount?: number;
  ActionCount?: number;
  successfulCount?: number;
  SuccessfulCount?: number;
  failedCount?: number;
  FailedCount?: number;
  successRate?: number;
  SuccessRate?: number;
}

interface HourlyActivityPatternCardProps {
  timeZoneLabel: string;
  /** Initial rows from progress detail (optional). */
  initialData?: HourlyActivityRow[] | null;
  className?: string;
}

const PERIOD_OPTIONS: { days: HourlyLookbackDays; labelKey: string }[] = [
  { days: 7, labelKey: 'hourlyPattern.period1Week' },
  { days: 30, labelKey: 'hourlyPattern.period1Month' },
  { days: 180, labelKey: 'hourlyPattern.period6Months' },
];

export const HourlyActivityPatternCard: React.FC<HourlyActivityPatternCardProps> = ({
  timeZoneLabel,
  initialData,
  className = '',
}) => {
  const { isDark } = useTheme();
  const { direction } = useLocale();
  const isRtl = direction === 'rtl';
  const chartFont = isRtl ? LEARNER_UI_PERSIAN_STACK : HOURLY_CHART_FONT_LATIN;
  const { t } = useTranslation('profile');
  const [lookbackDays, setLookbackDays] = useState<HourlyLookbackDays>(30);
  const [rows, setRows] = useState<HourlyActivityRow[]>(initialData ?? []);
  const [loading, setLoading] = useState(false);

  const loadPattern = useCallback(async (days: HourlyLookbackDays) => {
    setLoading(true);
    try {
      const data = await levelProgressionService.getHourlyActivityPattern(days);
      setRows(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (lookbackDays === 30 && initialData && initialData.length > 0) {
      setRows(initialData);
      return;
    }
    void loadPattern(lookbackDays);
  }, [lookbackDays, loadPattern, initialData]);

  const hasData = rows.some((r) => (r.actionCount ?? r.ActionCount ?? 0) > 0);

  const chartData = useMemo(
    () => ({
      labels: rows.map((item) => {
        const h = item.hour ?? item.Hour ?? 0;
        return `${h}:00`;
      }),
      datasets: [
        {
          label: t('hourlyPattern.chartActionsPerHour'),
          data: rows.map((item) => item.actionCount ?? item.ActionCount ?? 0),
          borderColor: '#3B82F6',
          backgroundColor: 'rgba(59, 130, 246, 0.12)',
          borderWidth: 2,
          fill: true,
          tension: 0.35,
          pointRadius: 4,
          pointHoverRadius: 6,
          pointBackgroundColor: '#3B82F6',
          pointBorderColor: isDark ? '#1f2937' : '#ffffff',
          pointBorderWidth: 2,
        },
      ],
    }),
    [rows, isDark, t]
  );

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          titleFont: { family: chartFont },
          bodyFont: { family: chartFont },
          callbacks: {
            label: (context: { dataIndex?: number; parsed?: { y: number } }) => {
              const i = context.dataIndex ?? 0;
              const row = rows[i];
              if (!row) {
                return t('hourlyPattern.tooltipActions', { count: context.parsed?.y ?? 0 });
              }
              const ac = row.actionCount ?? row.ActionCount ?? 0;
              const ok = row.successfulCount ?? row.SuccessfulCount ?? 0;
              const bad = row.failedCount ?? row.FailedCount ?? 0;
              const sr = row.successRate ?? row.SuccessRate ?? 0;
              return [
                t('hourlyPattern.tooltipActions', { count: ac }),
                t('hourlyPattern.tooltipSuccessful', { count: ok }),
                t('hourlyPattern.tooltipFailed', { count: bad }),
                t('hourlyPattern.tooltipSuccessRate', {
                  rate: typeof sr === 'number' ? sr.toFixed(1) : '0.0',
                }),
              ];
            },
          },
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: { precision: 0, color: isDark ? '#9ca3af' : '#6b7280', font: { family: chartFont, size: 10 } },
          grid: { color: isDark ? 'rgba(75, 85, 99, 0.3)' : 'rgba(229, 231, 235, 0.5)' },
        },
        x: {
          ticks: { color: isDark ? '#9ca3af' : '#6b7280', maxRotation: 45, font: { family: chartFont, size: 10 } },
          grid: { color: isDark ? 'rgba(75, 85, 99, 0.2)' : 'rgba(229, 231, 235, 0.4)' },
        },
      },
    }),
    [rows, isDark, t, chartFont]
  );

  return (
    <div className={`clay-card bg-white dark:bg-gray-800 p-4 sm:p-6 dark:border dark:border-gray-700 ${isRtl ? 'font-persian' : ''} ${className}`}>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-0.5 flex items-center sm:text-base">
            <TrendingUp className="w-5 h-5 mr-2 text-sky-500 shrink-0" />
            {t('hourlyPattern.title')}
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('hourlyPattern.description', { timeZone: timeZoneLabel })}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          {PERIOD_OPTIONS.map(({ days, labelKey }) => (
            <button
              key={days}
              type="button"
              onClick={() => setLookbackDays(days)}
              disabled={loading && lookbackDays === days}
              className={`min-h-[32px] rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                lookbackDays === days
                  ? 'border-sky-500 bg-sky-50 text-sky-900 dark:bg-sky-950/50 dark:text-sky-100'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200'
              }`}
            >
              {t(labelKey)}
            </button>
          ))}
        </div>
      </div>

      <div className="relative h-64 min-h-[14rem]">
        {loading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 dark:bg-gray-900/50 rounded-lg">
            <span className="inline-flex h-6 w-6 border-2 border-gray-200 border-t-sky-600 rounded-full animate-spin" />
          </div>
        )}
        {!loading && !hasData ? (
          <div className="flex h-full items-center justify-center text-sm text-gray-500 dark:text-gray-400">
            {t('hourlyPattern.noActivity')}
          </div>
        ) : (
          <Line data={chartData} options={chartOptions} />
        )}
      </div>
    </div>
  );
};
